import { Divider, Stack, Tooltip, Typography } from '@mui/material';
import type { AccessCondition, Role } from '@repo/api-gw-sdk';
import React, { useMemo } from 'react';

import {
  PermissionCategories,
  type PermissionDescriptor,
} from '@/contexts/permissions';
import { useRoles } from '@/contexts/useRoles';

import { PermissionIcon } from './permissionIcon';
import { PermissionOptionMenu } from './permissionOptionMenu';
import { RoleConditionTooltip } from './roleConditionTooltip';
import { getAssignedRoleCondition } from './utils';

export const RolePermissions = ({
  role,
  editMode,
  onChange,
  onConditional,
  hideUnauthorizedPermissions,
  hidePermissionGroups,
}:
  | {
      role: Role;
      hideUnauthorizedPermissions?: boolean;
      hidePermissionGroups?: boolean;
      editMode?: false;
      onChange?: undefined;
      onConditional?: undefined;
    }
  | {
      role: Role;
      onChange: (permission: PermissionDescriptor, value: boolean) => void;
      onConditional: (
        permission: PermissionDescriptor,
        roleCondition: AccessCondition | undefined
      ) => void;
      editMode: true;

      hideUnauthorizedPermissions?: false;
      hidePermissionGroups?: false;
    }) => {
  const { permissions } = useRoles();

  const shownCategories = useMemo(() => {
    const permissionsByCategory = Object.entries(permissions).reduce<
      Record<string, PermissionDescriptor[]>
    >((agg, [, value]) => {
      agg[value.category] = agg[value.category] || [];
      agg[value.category].push(value);
      return agg;
    }, {});

    return Object.entries(permissionsByCategory).filter(([, permissions]) => {
      if (hideUnauthorizedPermissions) {
        return permissions.some((x) =>
          role.permissionGrants.some((y) => y.permission === x.permissionType)
        );
      }

      return true;
    });
  }, [hideUnauthorizedPermissions, permissions, role]);

  return (
    <Stack direction='row' className='w-full' justifyContent='space-evenly'>
      {shownCategories.map(([category, permissions], index, arr) => {
        const groupedPermissions = permissions.reduce<
          Record<string, typeof permissions>
        >((acc, perm) => {
          acc[perm.group || ''] = acc[perm.group || ''] || [];
          acc[perm.group || ''].push(perm);
          return acc;
        }, {});

        return (
          <React.Fragment key={category}>
            <Stack className='w-full'>
              <Typography
                className='font-semibold cursor-default'
                sx={{
                  margin: '12px 0 24px',
                }}
              >
                {PermissionCategories[category]?.title || category}
              </Typography>
              <Stack>
                {Object.entries(groupedPermissions).map(
                  ([group, permissions]) => {
                    return (
                      <React.Fragment key={category + group}>
                        {group && !hidePermissionGroups && (
                          <Typography
                            className='mt-[24px] mb-[24px] cursor-default uppercase'
                            variant='body2'
                            sx={{
                              fontSize: '12px',
                            }}
                          >
                            {group}
                          </Typography>
                        )}
                        {permissions.map((permission) => {
                          const isPermissionAuthorized =
                            role.permissionGrants.some(
                              (x) => x.permission === permission.permissionType
                            );

                          if (
                            hideUnauthorizedPermissions &&
                            !isPermissionAuthorized
                          ) {
                            return null;
                          }

                          const assignedRoleCondition =
                            getAssignedRoleCondition(permission, role);

                          return (
                            <Stack
                              data-testid='role-permission'
                              direction='row'
                              gap='12px'
                              justifyContent='space-between'
                              key={permission.permissionType}
                              className='pb-[8px]'
                              sx={{
                                '&:hover i': {
                                  display: 'block !important',
                                },
                              }}
                            >
                              <Stack
                                direction='row'
                                gap='12px'
                                className='mt-[8px]'
                              >
                                <Typography className='cursor-default'>
                                  {permission.name}
                                </Typography>
                                {!hidePermissionGroups &&
                                  permission.description && (
                                    <Tooltip
                                      className='hidden'
                                      title={permission.description}
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            padding: '16px',
                                          },
                                        },
                                      }}
                                    >
                                      <i className=' material-symbols-help-outline-rounded h-[20px] w-[20px] p-[2px]' />
                                    </Tooltip>
                                  )}
                              </Stack>
                              <Stack direction='row' gap='20px'>
                                {!hideUnauthorizedPermissions &&
                                  assignedRoleCondition && (
                                    <RoleConditionTooltip
                                      role={role}
                                      roleCondition={assignedRoleCondition}
                                    />
                                  )}
                                {editMode && (
                                  <PermissionOptionMenu
                                    role={role}
                                    permission={permission}
                                    isPermissionAuthorized={
                                      isPermissionAuthorized
                                    }
                                    onBlocked={() =>
                                      onChange(permission, false)
                                    }
                                    onAllowed={() => onChange(permission, true)}
                                    createRoleCondition={() =>
                                      onConditional(permission, undefined)
                                    }
                                    assignRoleCondition={(roleCondition) =>
                                      onConditional(permission, roleCondition)
                                    }
                                    icon={
                                      <PermissionIcon
                                        editMode
                                        permission={permission}
                                        role={role}
                                        isPermissionAuthorized={
                                          isPermissionAuthorized
                                        }
                                      />
                                    }
                                  />
                                )}
                                {!editMode && (
                                  <PermissionIcon
                                    permission={permission}
                                    role={role}
                                    isPermissionAuthorized={
                                      isPermissionAuthorized
                                    }
                                  />
                                )}
                              </Stack>
                            </Stack>
                          );
                        })}
                      </React.Fragment>
                    );
                  }
                )}
              </Stack>
            </Stack>
            {index < arr.length - 1 && (
              <Divider orientation='vertical' flexItem className='mx-[40px]' />
            )}
          </React.Fragment>
        );
      })}
    </Stack>
  );
};
